import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  FiUsers, FiUserCheck, FiCalendar, FiDollarSign,
  FiTrendingUp, FiAlertCircle, FiCheckCircle, FiClock, FiArrowRight
} from 'react-icons/fi';
import './HR.css';

const HR = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [usersR, attendR, leaveR, salaryR] = await Promise.allSettled([
        api.get('/users'),
        api.get('/attendance/all-today'),
        api.get('/leaves/pending'),
        isAdmin ? api.get('/salary/all') : Promise.resolve({ data: { data: [] } }),
      ]);
      setStats({
        totalEmployees: usersR.value?.data?.users?.length || 0,
        presentToday:   attendR.value?.data?.data?.filter(a => a.status === 'present' || a.checkIn).length || 0,
        pendingLeaves:  leaveR.value?.data?.data?.length || 0,
        totalPayroll:   salaryR.value?.data?.data?.reduce((s, p) => s + (p.netSalary || 0), 0) || 0,
      });
    } catch { toast.error('Failed to load HR overview'); }
    setLoading(false);
  };

  const modules = [
    {
      title: 'Attendance',
      description: 'Track daily check-ins, check-outs, breaks and monthly attendance reports.',
      icon: <FiUserCheck size={28} />,
      color: '#667eea',
      link: '/attendance',
      stat: stats ? `${stats.presentToday} present today` : '—',
    },
    {
      title: 'Leave Management',
      description: 'Apply, approve or reject leave requests and track leave balances.',
      icon: <FiCalendar size={28} />,
      color: '#48bb78',
      link: '/leaves',
      stat: stats ? `${stats.pendingLeaves} pending requests` : '—',
    },
    {
      title: 'Salary & Payroll',
      description: 'Manage salary structures, generate payslips and process bulk payroll.',
      icon: <FiDollarSign size={28} />,
      color: '#ed8936',
      link: '/salary',
      stat: isAdmin && stats ? `₹${(stats.totalPayroll/1000).toFixed(0)}k this month` : 'View payslips',
    },
    {
      title: 'Holidays',
      description: 'View and manage national, regional and company holidays calendar.',
      icon: <FiCheckCircle size={28} />,
      color: '#9f7aea',
      link: '/holidays',
      stat: `${new Date().getFullYear()} calendar`,
    },
    {
      title: 'Team',
      description: 'View team members, roles and manage employee profiles.',
      icon: <FiUsers size={28} />,
      color: '#38b2ac',
      link: '/team',
      stat: stats ? `${stats.totalEmployees} employees` : '—',
    },
    {
      title: 'Onboarding',
      description: 'Track onboarding checklists for new employees from day one.',
      icon: <FiCheckCircle size={28} />,
      color: '#10b981',
      link: '/onboarding',
      stat: 'New hire checklist',
    },
    {
      title: 'Training & Certifications',
      description: 'Manage employee training programs, courses, and certifications.',
      icon: <FiTrendingUp size={28} />,
      color: '#6366f1',
      link: '/training',
      stat: 'Skills & growth',
    },
    {
      title: 'Shift / Roster',
      description: 'Plan and assign shifts, view weekly roster for all employees.',
      icon: <FiClock size={28} />,
      color: '#f59e0b',
      link: '/shifts',
      stat: 'Weekly schedule',
    },
  ];

  return (
    <div className="hr-page">
      <h1>HR Management</h1>

      {/* Stats Strip */}
      {!loading && stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 32 }}>
          {[
            { label: 'Total Employees', value: stats.totalEmployees, icon: <FiUsers />, color: '#667eea' },
            { label: 'Present Today',   value: stats.presentToday,   icon: <FiUserCheck />, color: '#48bb78' },
            { label: 'Pending Leaves',  value: stats.pendingLeaves,  icon: <FiAlertCircle />, color: '#f56565' },
            { label: 'Attendance Rate', value: stats.totalEmployees ? `${Math.round(stats.presentToday/stats.totalEmployees*100)}%` : '—', icon: <FiTrendingUp />, color: '#ed8936' },
          ].map(s => (
            <div key={s.label} className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 18 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: `${s.color}18`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#2d3748' }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#718096' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Module Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
        {modules.map(m => (
          <Link to={m.link} key={m.title} style={{ textDecoration: 'none' }}>
            <div className="card" style={{ cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div style={{ width: 52, height: 52, borderRadius: 12, background: `${m.color}15`, color: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {m.icon}
                </div>
                <FiArrowRight style={{ color: '#cbd5e0', fontSize: 18 }} />
              </div>
              <h3 style={{ margin: '0 0 6px', color: '#2d3748', fontSize: 17 }}>{m.title}</h3>
              <p style={{ margin: '0 0 14px', color: '#718096', fontSize: 13, lineHeight: 1.5 }}>{m.description}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: m.color }}>
                <FiClock size={12} />
                {m.stat}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default HR;
